import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  tap,
  map,
  catchError,
  throwError,
} from 'rxjs';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../environments/environment';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface User {
  id: string;
  username: string;
  email: string;
  full_name: string;
  first_name: string;
  last_name: string;
  roles: string[];
  workspaces: any[];
  assigned_companies?: AssignedCompany[];
  selected_company?: string;
  selected_branch?: string;
}

export interface AssignedCompany {
  id: string;
  main: boolean;
  name: string;
  is_active: boolean;
  owner: boolean;
  branches: CompanyBranch[];
}

export interface CompanyBranch {
  id: string;
  name: string;
  owner: boolean;
  main: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly API_BASE_URL = environment.apiBaseUrl;
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'user_data';
  private readonly CORRELATION_ID_KEY = 'correlation_id';

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(
    this.hasToken()
  );
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private currentUserSubject = new BehaviorSubject<User | null>(
    this.getStoredUser()
  );
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private snackBar: MatSnackBar
  ) {}

  login(credentials: LoginRequest): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Correlation-ID': this.generateCorrelationId(),
    });

    return this.http
      .post(`${this.API_BASE_URL}/auth/session/create`, credentials, {
        headers,
      })
      .pipe(
        tap((response: any) => {
          console.log('Login response:', response);
          if (response.data && response.data.token) {
            this.setToken(response.data.token);
            this.isAuthenticatedSubject.next(true);
            this.getCurrentUser().subscribe();
          } else {
            console.error('No token found in response:', response);
          }
        })
      );
  }

  logout(): Observable<any> {
    console.log('AuthService logout called');
    const headers = new HttpHeaders({
      'X-Correlation-ID': this.generateCorrelationId(),
      Authorization: `Bearer ${this.getToken()}`,
    });

    console.log('Making logout request to:', `${this.API_BASE_URL}/auth/session/destroy`);

    return this.http
      .get(`${this.API_BASE_URL}/auth/session/destroy`, { headers })
      .pipe(
        tap((response) => {
          console.log('Logout API response:', response);
          this.clearAuthData();
          this.router.navigate(['/login']);
        }),
        catchError((error) => {
          console.error('Logout API error:', error);
          // Even if API fails, clear local data and redirect
          this.clearAuthData();
          this.router.navigate(['/login']);
          return throwError(() => error);
        })
      );
  }

  setWorkspace(merchantId: string, branchId: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Correlation-ID': this.generateCorrelationId(),
      Authorization: `Bearer ${this.getToken()}`,
    });

    const body = {
      merchant_id: merchantId,
      branch_id: branchId,
    };

    return this.http
      .put(`${this.API_BASE_URL}/auth/session/setworkspace`, body, { headers })
      .pipe(
        tap((response) => {
          console.log('Workspace set successfully:', response);
        }),
        catchError((error) => {
          console.error('Set workspace error:', error);
          return throwError(() => error);
        })
      );
  }

  getCurrentUserSnapshot(): User | null {
    return this.currentUserSubject.value;
  }

  getCurrentUser(): Observable<User> {
    const headers = new HttpHeaders({
      'X-Correlation-ID': this.generateCorrelationId(),
      Authorization: `Bearer ${this.getToken()}`,
    });

    return this.http
      .get<any>(`${this.API_BASE_URL}/auth/session/current`, { headers })
      .pipe(
        tap((response: any) => {
          const user = response.data || response;
          this.currentUserSubject.next(user);
          this.storeUser(user);
        })
      );
  }

  getDashboard(): Observable<any> {
    const headers = new HttpHeaders({
      'X-Correlation-ID': this.generateCorrelationId(),
      Authorization: `Bearer ${this.getToken()}`,
    });

    return this.http
      .get(`${this.API_BASE_URL}/dashboard`, { headers })
      .pipe(map((response: any) => response.data || response));
  }

  private setToken(token: string) {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  getToken() {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  private hasToken() {
    return !!this.getToken();
  }

  private storeUser(user: User) {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  private getStoredUser() {
    const userData = localStorage.getItem(this.USER_KEY);
    return userData ? (JSON.parse(userData) as User) : null;
  }

  private clearAuthData() {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.CORRELATION_ID_KEY);
    this.isAuthenticatedSubject.next(false);
    this.currentUserSubject.next(null);
  }

  generateCorrelationId(): string {
    // Check if we already have a correlation ID for this session
    let correlationId = localStorage.getItem(this.CORRELATION_ID_KEY);
    
    if (!correlationId) {
      // Generate a new correlation ID if none exists
      correlationId = Math.random().toString(36).substring(2, 15);
      localStorage.setItem(this.CORRELATION_ID_KEY, correlationId);
      console.log('Generated new correlation ID:', correlationId);
    } else {
      console.log('Using existing correlation ID:', correlationId);
    }
    
    return correlationId;
  }

  getCorrelationId(): string {
    return localStorage.getItem(this.CORRELATION_ID_KEY) || '';
  }

  isLoggedIn() {
    return this.hasToken();
  }

  handle401Error() {
    console.log('401 Unauthorized - Logging out user');
    this.snackBar.open(
      'Your session has expired. Please log in again.',
      'Close',
      {
        duration: 5000,
        panelClass: ['error-snackbar'],
        horizontalPosition: 'center',
        verticalPosition: 'top',
      }
    );
    this.clearAuthData();
    this.router.navigate(['/login']);
  }
}
